import { PodhiveLogo } from "@/components/taxpeerlogo.component";
import AuthService from "@/lib/auth/AuthApi.service";
import BaseApiClient from "@/lib/api/baseApiClient.service";
import { useAuth } from "@/lib/contexts/auth.context";
import { useCookies } from "react-cookie";
import { useCallback, useEffect, useId, useState } from "react";
import useDeferredExecution from "@/lib/hooks/useDeferredExecution.hook";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { EmailStep } from "../components/loginEmail.component";
import { PasswordStep } from "../components/loginPassword.component";
import { GoogleSignInButton } from "../components/signInWithGoogle.component";
import { InAppBrowserBanner } from "../components/InAppBrowserBanner.component";
import useAltchaAutoToken from "@/lib/captcha/useAltchaAutoToken.hook";
import { AltchaWidget } from "@/lib/captcha/AltchaWidget.component";
import { useTranslation } from "react-i18next";
import { captureAuthException } from "@/lib/auth/captureAuthException";

const isValidEmail = (email: string) => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

/**
 * This is the login page of the application.
 * It is used to login the user.
 * There are two steps to login:
 * 1. Enter the email
 * 2. Enter the password
 * which are handled by the EmailStep and PasswordStep components.
 */
const LoginPage = () => {
	const { t } = useTranslation("common");
	const successIconId = useId();
	const [searchParams] = useSearchParams();
	const [step, setStep] = useState<"email" | "password">("email");
	const [formData, setFormData] = useState({
		email: "",
		password: "",
	});
	const [error, setError] = useState<string | null>(null);
	const [passwordResetSent, setPasswordResetSent] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const navigate = useNavigate();
	const { setAuthToken } = useAuth();
	const authService = AuthService.getInstance();
	const [, , removeCookie] = useCookies(["jwt_token"]);

	const { widgetRef, hasActiveToken, refreshNow } = useAltchaAutoToken({
		action: "login",
		intervalMs: 90_000,
	});

	// Read custom token once on mount and remove it immediately to prevent reuse
	const [customToken] = useState<string | null>(() => {
		const token = localStorage.getItem("customAuthenticationToken");
		if (token) {
			localStorage.removeItem("customAuthenticationToken");
			return token;
		}
		return null;
	});

	// Email prefilling and returnTo parameter handling
	useEffect(() => {
		// If a custom token is present, the custom login flow will handle navigation; skip email/returnTo handling here
		if (customToken) return;

		// Get email from URL parameter
		const emailFromURL = searchParams.get("email");

		// Get returnTo parameter for redirection after login
		const returnTo = searchParams.get("returnTo");

		// Store returnTo in localStorage if present
		if (returnTo) {
			localStorage.setItem("auth_redirect", returnTo);
		}

		// Handle email prefilling if provided in URL
		if (emailFromURL && isValidEmail(emailFromURL)) {
			setFormData((prev) => ({
				...prev,
				email: emailFromURL,
			}));
			// Automatically proceed to password step if email is provided
			setStep("password");
		}
	}, [searchParams, customToken]);

	/**
	 * Executes the custom token login.
	 * It will login the user with the custom token and redirect the user to the returnTo URL or the shop,
	 * depending on whether a returnTo URL or goToShop is present.
	 * @returns void
	 */
	const executeCustomTokenLogin = useCallback(async (): Promise<void> => {
		if (!customToken) return;
		setIsLoading(true);
		try {
			const response = await authService.loginWithCustomToken(customToken);
			await setAuthToken(response.token);

			const redirectUrl = localStorage.getItem("auth_redirect");
			const goToShop = localStorage.getItem("go_to_shop");

			if (redirectUrl) {
				localStorage.removeItem("auth_redirect");
				navigate(redirectUrl);
			} else if (goToShop) {
				localStorage.removeItem("go_to_shop");
				navigate("/shop");
			} else {
				navigate("/dashboard");
			}
		} catch (error: unknown) {
			console.log(
				"❌ [LOGIN] Custom token login failed, cleaning up state:",
				error,
			);
			captureAuthException(error, "login_page_custom_token");
			BaseApiClient.resetInstance();
			removeCookie("jwt_token", {
				path: "/",
				secure: process.env.NODE_ENV === "production",
				sameSite: "strict",
			});
			console.log(
				"🧹 [LOGIN] Cleared stale auth state after failed custom token login",
			);
			if (error instanceof Error) {
				setError(error.message);
			} else {
				setError("Fehler beim Login mit Token");
			}
		} finally {
			setIsLoading(false);
		}
	}, [authService, customToken, navigate, removeCookie, setAuthToken]);

	/**
	 * Waits for the captcha token to be available, then executes the custom token login automatically.
	 */
	const { run: runCustomTokenLogin } = useDeferredExecution({
		condition: hasActiveToken,
		canRun: Boolean(customToken),
		timeoutMs: 15_000,
		onTimeout: () => setError(t("captcha.botDetected")),
		execute: executeCustomTokenLogin,
	});

	useEffect(() => {
		if (customToken) {
			runCustomTokenLogin();
		}
	}, [customToken, runCustomTokenLogin]);

	const handleEmailSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		// Validate email before proceeding
		if (!isValidEmail(formData.email)) {
			setError(t("errors.invalidEmail"));
			return;
		}
		setError(null);
		setStep("password");
	};

	const handleForgotPassword = async () => {
		// Validate email before sending reset
		if (!isValidEmail(formData.email)) {
			setError(t("errors.invalidEmail"));
			return;
		}
		setError(null);
		setPasswordResetSent(false);
		try {
			await authService.forgotPassword(formData.email);
			setPasswordResetSent(true);
			// Auto-hide success message after 10 seconds
			setTimeout(() => setPasswordResetSent(false), 10000);
		} catch (error) {
			console.error("Failed to send password reset:", error);
			captureAuthException(error, "login_page_forgot_password");
			setError(t("errors.passwordResetFailed"));
		}
	};

	// Extracted login executor so we can call it from submit handler and auto-retry effect
	const executePasswordLogin = useCallback(async (): Promise<void> => {
		setError(null);
		setIsLoading(true);

		try {
			// Store token centrally for interceptor to attach on auth/login
			const response = await authService.login(formData, refreshNow);

			// Set auth token and wait for user data to be loaded
			await setAuthToken(response.token);

			// Get stored redirect URL
			const redirectUrl = localStorage.getItem("auth_redirect");
			const goToShop = localStorage.getItem("go_to_shop");

			// Navigate immediately - no need for timeout since setAuthToken is now properly awaited
			if (redirectUrl) {
				// Clear the stored redirect after using it
				localStorage.removeItem("auth_redirect");
				navigate(redirectUrl);
			} else if (goToShop) {
				localStorage.removeItem("go_to_shop");
				navigate("/shop");
			} else {
				// Default navigation to dashboard
				navigate("/dashboard");
			}
		} catch (error: unknown) {
			console.log("❌ [LOGIN] Login failed, cleaning up state:", error);
			captureAuthException(error, "login_page_password");

			// Reset BaseApiClient on login failure to clear any stale state
			BaseApiClient.resetInstance();

			// Also clear any stale JWT cookie that might be causing issues
			removeCookie("jwt_token", {
				path: "/",
				secure: process.env.NODE_ENV === "production",
				sameSite: "strict",
			});

			console.log("🧹 [LOGIN] Cleared stale auth state after failed login");

			if (error instanceof Error) {
				setError(error.message);
			} else {
				setError(t("errors.loginFailed"));
			}
		} finally {
			setIsLoading(false);
		}
	}, [
		authService,
		formData,
		navigate,
		removeCookie,
		setAuthToken,
		t,
		refreshNow,
	]);

	const { run: runPasswordLogin, isBusy: isPasswordBusy } =
		useDeferredExecution({
			condition: hasActiveToken,
			canRun: Boolean(formData.password),
			timeoutMs: 15_000,
			onTimeout: () => setError(t("captcha.botDetected")),
			execute: executePasswordLogin,
		});

	/**
	 * Handles the submission of the password step.
	 * @param e - The form event.
	 */
	const handlePasswordSubmit = async (e: React.SyntheticEvent) => {
		// Always prevent default submission and stop propagation first so Enter doesn't submit
		e.preventDefault();
		e.stopPropagation();
		runPasswordLogin();
	};

	return (
		<div className="flex min-h-screen flex-col items-center justify-center bg-card p-4">
			<div className="w-full max-w-[400px] space-y-8">
				{/* Logo */}
				<div className="flex justify-center">
					<PodhiveLogo width={125} bildmarke={false} />
				</div>
				<InAppBrowserBanner />

				{error && (
					<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
						{error}
					</div>
				)}

				{passwordResetSent && (
					<div className="rounded-lg bg-green-500/10 border-2 border-green-500/50 p-4 text-center space-y-2">
						<div className="flex items-center justify-center gap-2">
							<svg
								className="w-6 h-6 text-green-600 dark:text-green-400"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
								role="img"
								aria-labelledby={successIconId}
							>
								<title id={successIconId}>Success</title>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
								/>
							</svg>
							<p className="font-semibold text-green-700 dark:text-green-300">
								{t("passwordResetSent")}
							</p>
						</div>
						<p className="text-sm text-green-600 dark:text-green-400">
							{t("passwordResetSentDescription")}
						</p>
					</div>
				)}

				{/* Login Steps */}
				{step === "email" ? (
					<EmailStep
						error={error}
						email={formData.email}
						onEmailChange={(email) => {
							setFormData((prev) => ({ ...prev, email }));
							// Clear error when user starts typing a valid email
							if (error && isValidEmail(email)) {
								setError(null);
							}
						}}
						onSubmit={handleEmailSubmit}
					/>
				) : (
					<PasswordStep
						email={formData.email}
						password={formData.password}
						onPasswordChange={(password) =>
							setFormData((prev) => ({ ...prev, password }))
						}
						onSubmit={handlePasswordSubmit}
						onEditEmail={() => setStep("email")}
						isLoading={isLoading || isPasswordBusy}
						onForgotPassword={handleForgotPassword}
					/>
				)}
			</div>

			{/* Keep Altcha mounted so email login and the post-Google JWT upgrade can refresh a token. */}
			<div className="w-full max-w-[400px]">
				<AltchaWidget widgetRef={widgetRef} hasActiveToken={hasActiveToken} />
			</div>

			<div className="w-full max-w-[400px] space-y-8 mt-8">
				<div className="relative">
					<div className="absolute inset-0 flex items-center">
						<div className="w-full border-t border-border" />
					</div>
					<div className="relative flex justify-center text-sm uppercase">
						<span className="bg-backgroundSecondary px-4 text-muted-foreground">
							{t("or")}
						</span>
					</div>
				</div>

				<GoogleSignInButton refreshCaptcha={refreshNow} />

				{/* Footer */}
				<div className="flex justify-center space-x-4 text-sm text-muted-foreground">
					<Link to="/agb">{t("footer.terms")}</Link>
					<Link to="/datenschutz">{t("footer.privacy")}</Link>
				</div>
			</div>
		</div>
	);
};

export default LoginPage;
